<template>
  <layout toolbar_off="1" title="Visualizar Venda" :rotas="rotas">
    <v-container fluid class="sale-view-page" :class="{ 'sale-view-page-dark': isDark }">
      <div class="sale-view-topbar">
        <v-btn icon :color="$theme.primary" @click="goBack">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <div class="sale-view-title">
          <strong>{{ saleClientName }}</strong>
          <small>
            {{ get_gestordepedido.tipo }} #{{ get_gestordepedido.id_transacao }}
            · {{ get_gestordepedido.status_do_pedido || "sem status" }}
          </small>
        </div>
        <v-spacer />
        <v-chip small :color="$theme.primary" dark>{{ paymentSummary }}</v-chip>
      </div>

      <div v-if="loading" class="sale-view-loading">
        <v-progress-circular indeterminate color="primary" />
        <span>Carregando venda...</span>
      </div>
      <div v-else class="sale-view-shell">
        <aside class="sale-view-chat">
          <sale-collaboration-panel />
        </aside>

        <main class="sale-view-main">
          <sale-body
            :openReceiptEmitter="initPayment"
            :printReceipt="printReceipt"
            :printDeliveryNote="printDeliveryNote"
            :printExitNote="printExitNote"
            :printTechnicalDrawing="printTechnicalDrawing"
            :configureDeliveryNote="configureDeliveryNote"
            :confirmCancel="confirmCancel"
            :sendToPdv="sendToPdv"
            :initPayment="initPayment"
            :hideActions="true"
          />
        </main>

        <aside class="sale-view-actions">
          <span class="actions-label">Opções da venda</span>
          <button v-if="canShowApprovalActions" class="sale-action approval-approve" @click="openApprovalWizard('approved')">
            <span class="sale-action-icon">
              <v-icon size="19">mdi-check-decagram-outline</v-icon>
            </span>
            <span class="sale-action-copy">
              <strong>Aprovar produção</strong>
              <small>Enviar para produção com obra ou polo</small>
            </span>
          </button>
          <button v-if="canShowApprovalActions" class="sale-action approval-reject" @click="openApprovalWizard('rejected')">
            <span class="sale-action-icon">
              <v-icon size="19">mdi-archive-arrow-down-outline</v-icon>
            </span>
            <span class="sale-action-copy">
              <strong>Reprovar orçamento</strong>
              <small>Arquivar esta proposta</small>
            </span>
          </button>
          <button class="sale-action" @click="sendToPdv(get_gestordepedido._id)">
            <span class="sale-action-icon">
              <v-icon size="19">mdi-calculator</v-icon>
            </span>
            <span class="sale-action-copy">
              <strong>Abrir no PDV</strong>
              <small>Continuar esta venda no caixa</small>
            </span>
          </button>
          <button class="sale-action" @click="configureDeliveryNote">
            <span class="sale-action-icon">
              <v-icon size="19">mdi-cloud-print-outline</v-icon>
            </span>
            <span class="sale-action-copy">
              <strong>Imprimir nota de entrega</strong>
              <small>Nova nota ou reimpressão</small>
            </span>
          </button>
          <button class="sale-action" @click="openMovementDialog('unity')">
            <span class="sale-action-icon">
              <v-icon size="19">mdi-source-branch-sync</v-icon>
            </span>
            <span class="sale-action-copy">
              <strong>Enviar para outra unidade</strong>
              <small>Movimentar peça ou montagem entre polos</small>
            </span>
          </button>
          <button class="sale-action" @click="openMovementDialog('company')">
            <span class="sale-action-icon">
              <v-icon size="19">mdi-swap-horizontal-bold</v-icon>
            </span>
            <span class="sale-action-copy">
              <strong>Enviar para outra empresa</strong>
              <small>Compartilhar e emitir nota de saída</small>
            </span>
          </button>
          <button class="sale-action" @click="initPayment">
            <span class="sale-action-icon">
              <v-icon size="19">mdi-printer-outline</v-icon>
            </span>
            <span class="sale-action-copy">
              <strong>{{ get_gestordepedido.status_do_pagamento === "pago" ? "Venda paga" : "Imprimir recibo" }}</strong>
              <small>Comprovante de pagamento</small>
            </span>
          </button>
          <button class="sale-action" @click="printReceipt(get_gestordepedido)">
            <span class="sale-action-icon">
              <v-icon size="19">mdi-receipt-outline</v-icon>
            </span>
            <span class="sale-action-copy">
              <strong>Imprimir venda</strong>
              <small>Resumo completo da venda</small>
            </span>
          </button>
          <button class="sale-action" @click="printTechnicalDrawing">
            <span class="sale-action-icon">
              <v-icon size="19">mdi-ruler-square-compass</v-icon>
            </span>
            <span class="sale-action-copy">
              <strong>Imprimir desenho técnico</strong>
              <small>Documento técnico do pedido</small>
            </span>
          </button>
          <button v-if="!get_gestordepedido.external_movimentation" class="sale-action" @click="prepareDelivery(get_gestordepedido)">
            <span class="sale-action-icon">
              <v-icon size="19">mdi-calendar</v-icon>
            </span>
            <span class="sale-action-copy">
              <strong>Criar agendamento</strong>
              <small>Programar visita ou entrega</small>
            </span>
          </button>
          <button
            v-if="get_gestordepedido.status_do_pagamento === 'pendente' && get_gestordepedido.status !== 'cancelado' && get_gestordepedido.status !== 'concluído'"
            class="sale-action danger"
            @click="confirmCancel"
          >
            <span class="sale-action-icon">
              <v-icon size="19">mdi-cancel</v-icon>
            </span>
            <span class="sale-action-copy">
              <strong>Cancelar venda</strong>
              <small>Encerrar esta venda sem concluir</small>
            </span>
          </button>
        </aside>
      </div>

      <div class="sale-print-layer" aria-hidden="true">
        <delivery-note-to-print
          v-show="false"
          id="print-note"
          class="print-unbreak"
          :lastSell="get_gestordepedido"
          :getLoggedUser="getLoggedUser"
        />
        <sale-budget-print
          :lastSell="get_gestordepedido"
          :getLoggedUser="getLoggedUser"
        />
        <sale-technical-drawing-print
          :lastSell="get_gestordepedido"
          :getLoggedUser="getLoggedUser"
        />
        <exit-note
          id="print-exit-note"
          :lastSell="get_gestordepedido"
          :movement="selectedExitMovement || {}"
          :getLoggedUser="getLoggedUser"
        />
      </div>
      <ModalChangeAddressFromViewSale
        :printDeliveryNote="printDeliveryNote"
        ref="modalChangeDelivery"
      />
      <ModalFechamento
        ref="modalFechamento"
        :payment_sys_config="payment_sys_config"
      />
      <EventDialog ref="eventDialog" origin="sale-view" />
      <sale-approval-wizard
        ref="approvalWizard"
        :sale="get_gestordepedido"
        @saved="onApprovalSaved"
      />
      <sale-movement-dialog
        ref="movementDialog"
        :sale="get_gestordepedido"
        @saved="onMovementSaved"
      />
    </v-container>
  </layout>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ModalFechamento from "../components/SaleStages/ModalFechamento.vue";
import ModalChangeAddressFromViewSale from "@/apps/27#Caixas/views/components/ModalChangeDeliveryFromViewSale.vue";
import SaleBody from "@/apps/GestorDePedido/components/viewSale/saleBody.vue";
import SaleCollaborationPanel from "@/apps/GestorDePedido/components/viewSale/SaleCollaborationPanel.vue";
import deliveryNoteToPrint from "@/apps/GestorDePedido/components/viewSale/documentToPrint/deliveryNote.vue";
import SaleBudgetPrint from "@/apps/GestorDePedido/components/viewSale/documentToPrint/SaleBudgetPrint.vue";
import SaleTechnicalDrawingPrint from "@/apps/GestorDePedido/components/viewSale/documentToPrint/SaleTechnicalDrawingPrint.vue";
import ExitNote from "@/apps/GestorDePedido/components/viewSale/documentToPrint/exitNote.vue";
import EventDialog from "@/apps/ExternalMovimentation/components/EventDialog.vue";
import SaleApprovalWizard from "@/apps/GestorDePedido/components/SaleStages/SaleApprovalWizard.vue";
import SaleMovementDialog from "@/apps/GestorDePedido/components/SaleStages/SaleMovementDialog.vue";
import {
  printBudget,
  printDeliveryNote as printDeliveryNoteDocument,
  printExitNote as printExitNoteDocument,
  printTechnicalDrawing as printTechnicalDrawingDocument
} from "@/apps/shared/services/printSaleDocuments";

export default {
  components: {
    ModalFechamento,
    ModalChangeAddressFromViewSale,
    SaleBody,
    SaleCollaborationPanel,
    deliveryNoteToPrint,
    SaleBudgetPrint,
    SaleTechnicalDrawingPrint,
    ExitNote,
    EventDialog,
    SaleApprovalWizard,
    SaleMovementDialog
  },
  data() {
    return {
      loading: true,
      payment_sys_config: {
        money: { active: true, tax_porcentage: 0 },
        card: { active: false, tax_porcentage: 0 },
        pix: { active: false, tax_porcentage: 0 },
        debit: { active: false, tax_porcentage: 0 },
        finishing: { active: false, tax_porcentage: 30 },
        custom_methods: []
      },
      rotas: [
        { name: "Inicio", path: "/dashboard" },
        { name: "Gestor de Vendas", path: "/gestordepedidos" },
        { name: "Venda", path: this.$route.fullPath }
      ],
      selectedExitMovement: null
    };
  },
  computed: {
    ...mapGetters(["get_gestordepedido", "getLoggedUser"]),
    isDark() {
      return localStorage.dark === "true";
    },
    saleClientName() {
      return this.get_gestordepedido.compra && this.get_gestordepedido.compra.cliente
        ? this.get_gestordepedido.compra.cliente.nome
        : "Cliente nao informado";
    },
    paymentSummary() {
      const compra = this.get_gestordepedido.compra || {};
      const total = Number(compra.preco_total || 0);
      const paid = Number(compra.total_recebido || 0);
      const remaining = Math.max(total - paid, 0);
      if (remaining <= 0 && total > 0) return `Pago ${this.$helper.formataSaldo(paid)}`;
      return `Pago ${this.$helper.formataSaldo(paid)} · Falta ${this.$helper.formataSaldo(remaining)}`;
    },
    canShowApprovalActions() {
      const sale = this.get_gestordepedido || {};
      const decision = String(
        sale.approval && (sale.approval.decision || sale.approval.status) || ""
      ).toLowerCase();
      if (["approved", "rejected", "aprovado", "reprovado", "arquivado"].includes(decision)) return false;

      const status = String(sale.status_do_pedido || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();

      return ![
        "em producao",
        "arquivado",
        "arquivada",
        "aprovado",
        "aprovada",
        "reprovado",
        "reprovada",
        "recusado",
        "recusada",
        "cancelado",
        "cancelada",
        "concluido",
        "finalizado",
        "entregue"
      ].includes(status);
    }
  },
  methods: {
    ...mapActions([
      "createConfirmAction",
      "createGlobalMessage",
      "listar_gestordepedidos"
    ]),
    normalizePaymentSysConfig(config = {}) {
      return {
        money: { tax_porcentage: 0, ...(config.money || {}), active: true },
        card: { active: false, tax_porcentage: 0, ...(config.card || {}) },
        pix: { active: false, tax_porcentage: 0, ...(config.pix || {}) },
        debit: { active: false, tax_porcentage: 0, ...(config.debit || {}) },
        finishing: { active: false, tax_porcentage: 30, ...(config.finishing || {}) },
        custom_methods: Array.isArray(config.custom_methods) ? config.custom_methods : []
      };
    },
    async loadPaymentConfig() {
      try {
        const response = await this.$run("marketplaces/get-config-payment-sys");
        this.payment_sys_config = this.normalizePaymentSysConfig(response.data.payment_sys_config);
      } catch (error) {
        this.payment_sys_config = this.normalizePaymentSysConfig(this.payment_sys_config);
      }
    },
    async loadSale() {
      this.loading = true;
      try {
        const response = await this.$run("transacoes/view-transaction", {
          _id: this.$route.params.id
        });
        const sale = response.data || {};
        sale.comments = sale.comments ? sale.comments.reverse() : [];
        this.$store.commit("set_gestordepedido", sale);
        this.$store.commit("setFiles", sale.arquivos);
        this.$store.commit("setLinks", sale.imagens);
        this.$store.commit("set_modal_view_gestordepedido", false);
      } catch (error) {
        this.createGlobalMessage({
          type: "error",
          message: error.response?.data?.message || "Nao foi possivel carregar a venda.",
          timeout: 5000
        });
        this.$router.push("/gestordepedidos");
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    confirmCancel() {
      this.createConfirmAction({
        message: "Deseja cancelar esta venda?",
        icone: "mdi-close-circle",
        isScoped: true,
        action: () => this.cancelSale(),
        action_value: "ok"
      });
    },
    cancelSale() {
      this.$run("caixas/cancel-sale", {
        ...this.get_gestordepedido,
        novo_status: "cancelada"
      }).then(() => {
        this.createGlobalMessage({ type: "success", message: "A venda foi cancelada", timeout: 6000 });
        this.listar_gestordepedidos();
        this.$router.push("/gestordepedidos");
      });
    },
    sendToPdv(id) {
      this.$router.push(`/atendimento?t_id=${id}`);
    },
    async initPayment() {
      if (this.get_gestordepedido.compra && this.get_gestordepedido.compra.produtos && this.get_gestordepedido.compra.produtos.length) {
        await this.loadPaymentConfig();
        this.$refs.modalFechamento.open();
      } else {
        this.createGlobalMessage({
          type: "error",
          message: "Nao e possivel efetuar uma venda sem produtos.",
          timeout: 6000
        });
      }
    },
    configureDeliveryNote() {
      this.$refs.modalChangeDelivery.open();
    },
    prepareDelivery(sale) {
      this.$refs.eventDialog.open(sale, "sale");
    },
    openApprovalWizard(decision) {
      this.$refs.approvalWizard.open(decision);
    },
    onApprovalSaved(sale) {
      if (sale && sale._id) {
        this.$store.commit("set_gestordepedido", sale);
      }
    },
    openMovementDialog(mode) {
      this.$refs.movementDialog.open(mode);
    },
    async onMovementSaved(payload) {
      if (payload.sale && payload.sale._id) {
        this.$store.commit("set_gestordepedido", payload.sale);
      }
      await this.$nextTick();
      this.printExitNote(payload.movement);
    },
    printExitNote(movement) {
      this.selectedExitMovement = movement;
      this.$nextTick(() => {
        printExitNoteDocument({
          notify: this.createGlobalMessage,
          context: { vuetify: this.$vuetify }
        });
      });
    },
    printReceipt(compra) {
      this.$store.commit("set_gestordepedido", compra || this.get_gestordepedido);
      this.$nextTick(() => {
        printBudget({
          notify: this.createGlobalMessage,
          context: { vuetify: this.$vuetify }
        });
      });
    },
    printTechnicalDrawing() {
      const produtos =
        (this.get_gestordepedido.compra &&
          this.get_gestordepedido.compra.produtos) ||
        [];
      const hasFicha = produtos.some(
        p => Array.isArray(p.ficha_tecnica) && p.ficha_tecnica.length
      );
      if (!hasFicha) {
        this.createGlobalMessage({
          type: "error",
          message: "Esta venda nao possui pecas com ficha tecnica para gerar o desenho.",
          timeout: 5000
        });
        return;
      }
      printTechnicalDrawingDocument({
        notify: this.createGlobalMessage,
        context: { vuetify: this.$vuetify }
      });
    },
    printDeliveryNote() {
      printDeliveryNoteDocument({
        notify: this.createGlobalMessage,
        context: { vuetify: this.$vuetify }
      });
    }
  },
  created() {
    this.loadPaymentConfig();
    this.loadSale();
  },
  watch: {
    "$route.params.id"() {
      this.loadSale();
    }
  }
};
</script>

<style scoped>
.sale-view-page {
  background: var(--v-background-base, #f3edda);
  bottom: 0;
  left: 0;
  min-height: 100vh;
  padding: 10px;
  position: relative;
  right: 0;
}
.sale-view-page-dark {
  background: #0b141a;
  color: #e9edef;
}
.sale-view-topbar {
  align-items: center;
  background: var(--v-surface-base, #ffffff);
  border: 1px solid rgba(120, 106, 78, 0.16);
  border-radius: 14px;
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  min-height: 56px;
  padding: 8px 12px;
}
.sale-view-title strong,
.sale-view-title small {
  display: block;
}
.sale-view-title strong {
  color: var(--v-primary-base, #128C7E);
  font-size: 15px;
}
.sale-view-title small {
  color: #7a7a7a;
  font-size: 12px;
}
.sale-view-page-dark .sale-view-topbar,
.sale-view-page-dark .sale-view-chat,
.sale-view-page-dark .sale-view-main {
  background: #111b21;
  border-color: #26343c;
}
.sale-view-page-dark .sale-view-main {
  box-shadow: inset 0 0 0 1px #26343c;
}
.sale-view-page-dark .sale-view-title strong {
  color: #25D366;
}
.sale-view-page-dark .sale-view-title small,
.sale-view-page-dark .actions-label {
  color: #aebac1;
}
.sale-view-shell {
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(280px, 3fr) minmax(0, 6fr) minmax(250px, 3fr);
  height: calc(100vh - 122px);
  overflow: hidden;
}
.sale-view-chat {
  background: var(--v-surface-base, #ffffff);
  border: 1px solid rgba(120, 106, 78, 0.16);
  border-radius: 14px;
  min-height: 0;
  overflow: hidden;
}
.sale-view-main {
  background: var(--v-surface-base, #ffffff);
  border: 1px solid rgba(120, 106, 78, 0.16);
  border-radius: 14px;
  min-height: 0;
  overflow: auto;
}
.sale-view-actions {
  background: transparent;
  min-height: 0;
  overflow: auto;
  padding: 2px 2px 10px;
}
.actions-label {
  color: #7a7a7a;
  display: block;
  font-size: 12px;
  margin: 2px 0 8px;
}
.sale-action {
  align-items: center;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(120, 106, 78, 0.14);
  border-radius: 12px;
  color: #3f3f3f;
  cursor: pointer;
  display: flex;
  gap: 10px;
  margin-bottom: 8px;
  min-height: 58px;
  padding: 10px 12px;
  text-align: left;
  transition: 0.18s ease;
  width: 100%;
}
.sale-view-page-dark .sale-action {
  background: #182229;
  border-color: #26343c;
  color: #e9edef;
}
.sale-view-page-dark .sale-action ::v-deep .v-icon {
  color: #aebac1 !important;
}
.sale-view-page-dark .sale-action:hover ::v-deep .v-icon {
  color: #25D366 !important;
}
.sale-action:hover {
  background: var(--v-surface-base, #ffffff);
  border-color: var(--v-primary-base, #128C7E);
  transform: translateX(-2px);
}
.sale-view-page-dark .sale-action:hover {
  background: #202c33;
  border-color: #25D366;
}
.sale-action-icon {
  align-items: center;
  background: #f0f3f5;
  border-radius: 8px;
  color: #61707d;
  display: flex;
  flex: 0 0 36px;
  height: 36px;
  justify-content: center;
  width: 36px;
}
.sale-action:hover .sale-action-icon {
  background: #e3f4ee;
  color: #128c7e;
}
.sale-action-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.sale-action-copy strong {
  color: #1f2933;
  font-size: 13px;
  line-height: 1.25;
}
.sale-action-copy small {
  color: #66717d;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.25;
}
.sale-view-page-dark .sale-action-icon {
  background: #202c33;
  color: #aebac1;
}
.sale-view-page-dark .sale-action:hover .sale-action-icon {
  background: #0f3d31;
  color: #8de7c1;
}
.sale-view-page-dark .sale-action-copy strong {
  color: #eef4f1;
}
.sale-view-page-dark .sale-action-copy small {
  color: #8ea39a;
}
.sale-action.danger {
  color: #d84343;
}
.sale-action.danger .sale-action-icon {
  background: #fff1f1;
  color: #d84343;
}
.sale-action.danger .sale-action-copy strong {
  color: #d84343;
}
.sale-action.approval-approve {
  border-color: rgba(22, 138, 83, 0.35);
}
.sale-action.approval-approve .sale-action-icon {
  background: #e6f6ee;
  color: #168a53;
}
.sale-action.approval-approve .sale-action-copy strong {
  color: #168a53;
}
.sale-action.approval-reject {
  border-color: rgba(160, 90, 0, 0.35);
}
.sale-action.approval-reject .sale-action-icon {
  background: #fff5df;
  color: #a05a00;
}
.sale-action.approval-reject .sale-action-copy strong {
  color: #a05a00;
}
.sale-view-page-dark .sale-action.danger .sale-action-icon {
  background: #3a1f23;
  color: #ff8a8a;
}
.sale-view-page-dark .sale-action.approval-approve {
  background: #14261d;
  border-color: #26734d;
}
.sale-view-page-dark .sale-action.approval-approve .sale-action-icon {
  background: #123826;
  color: #8de7c1;
}
.sale-view-page-dark .sale-action.approval-approve .sale-action-copy strong {
  color: #bdf6d7;
}
.sale-view-page-dark .sale-action.approval-approve .sale-action-copy small {
  color: #91c9ab;
}
.sale-view-page-dark .sale-action.approval-reject {
  background: #2a2112;
  border-color: #8d5d12;
}
.sale-view-page-dark .sale-action.approval-reject .sale-action-icon {
  background: #392c13;
  color: #ffd27a;
}
.sale-view-page-dark .sale-action.approval-reject .sale-action-copy strong {
  color: #ffe0a3;
}
.sale-view-page-dark .sale-action.approval-reject .sale-action-copy small {
  color: #d4b477;
}
.sale-view-loading {
  align-items: center;
  color: #786a4e;
  display: flex;
  gap: 12px;
  height: 100%;
  justify-content: center;
}
.sale-view-chat ::v-deep .sale-whatsapp-panel {
  border: 0;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  height: 100%;
}
.sale-view-chat ::v-deep .sale-whatsapp-stream {
  flex: 1;
  max-height: none;
  min-height: 0;
}
.sale-view-chat ::v-deep .sale-whatsapp-composer {
  flex-shrink: 0;
}
.sale-view-main ::v-deep .windows-style-content {
  max-height: none !important;
}
.sale-view-main ::v-deep .tenant-transfer-panel {
  background: transparent;
  padding-left: 10px;
  padding-right: 10px;
}
.sale-view-main ::v-deep .transfer-main {
  border-color: rgba(120, 106, 78, 0.16);
}
.sale-view-page-dark .sale-view-main ::v-deep .windows-style-content,
.sale-view-page-dark .sale-view-main ::v-deep .tenant-transfer-panel {
  background: #111b21;
  border-color: #111b21 !important;
  color: #e9edef;
}
.sale-view-page-dark .sale-view-main ::v-deep .windows-style-content {
  border-left: 0 !important;
  border-right: 0 !important;
  box-shadow: none !important;
}
.sale-view-page-dark .sale-view-main ::v-deep .v-list,
.sale-view-page-dark .sale-view-main ::v-deep .v-list-item,
.sale-view-page-dark .sale-view-main ::v-deep .v-list-item__title,
.sale-view-page-dark .sale-view-main ::v-deep .v-list-item__subtitle,
.sale-view-page-dark .sale-view-main ::v-deep .fonte,
.sale-view-page-dark .sale-view-main ::v-deep .fonteMini,
.sale-view-page-dark .sale-view-main ::v-deep span,
.sale-view-page-dark .sale-view-main ::v-deep td,
.sale-view-page-dark .sale-view-main ::v-deep th {
  color: #e9edef !important;
}
.sale-view-page-dark .sale-view-main ::v-deep .grey--text {
  color: #aebac1 !important;
}
.sale-view-page-dark .sale-view-main ::v-deep .v-data-table,
.sale-view-page-dark .sale-view-main ::v-deep table,
.sale-view-page-dark .sale-view-main ::v-deep tr {
  background: transparent !important;
}
.sale-view-page-dark .sale-view-main ::v-deep .v-input__slot,
.sale-view-page-dark .sale-view-main ::v-deep .v-text-field--solo .v-input__slot,
.sale-view-page-dark .sale-view-main ::v-deep .v-text-field--solo-inverted .v-input__slot,
.sale-view-page-dark .sale-view-main ::v-deep textarea,
.sale-view-page-dark .sale-view-main ::v-deep input {
  background: #182229 !important;
  color: #e9edef !important;
}
.sale-view-page-dark .sale-view-main ::v-deep .v-label,
.sale-view-page-dark .sale-view-main ::v-deep .v-select__selection,
.sale-view-page-dark .sale-view-main ::v-deep .v-input input::placeholder,
.sale-view-page-dark .sale-view-main ::v-deep textarea::placeholder {
  color: #aebac1 !important;
}
.sale-view-page-dark .sale-view-main ::v-deep .transfer-main,
.sale-view-page-dark .sale-view-main ::v-deep .movement-row {
  background: #182229;
  border-color: #26343c;
}
.sale-view-page-dark .sale-view-main ::v-deep .transfer-head,
.sale-view-page-dark .sale-view-main ::v-deep .transfer-actions {
  background: #202c33;
  border-color: #26343c;
}
.sale-view-page-dark .sale-view-main ::v-deep .movement-details,
.sale-view-page-dark .sale-view-main ::v-deep .event-strip,
.sale-view-page-dark .sale-view-main ::v-deep .transfer-title small,
.sale-view-page-dark .sale-view-main ::v-deep .action-step small {
  color: #aebac1 !important;
}
.sale-view-page-dark .sale-view-chat ::v-deep .v-icon {
  color: #aebac1;
}
.sale-view-page-dark .sale-view-chat ::v-deep .topbar-actions .v-icon,
.sale-view-page-dark .sale-view-chat ::v-deep .sale-whatsapp-composer .v-icon {
  color: #25D366 !important;
}
.sale-view-page-dark .sale-view-chat ::v-deep .v-btn--disabled .v-icon {
  color: #607d75 !important;
}
.sale-print-layer {
  height: 0;
  left: -99999px;
  overflow: hidden;
  pointer-events: none;
  position: absolute;
  top: -99999px;
  width: 0;
}
.sale-view-page ::v-deep ::-webkit-scrollbar,
.sale-view-page::-webkit-scrollbar {
  height: 10px;
  width: 10px;
}
.sale-view-page ::v-deep ::-webkit-scrollbar-track,
.sale-view-page::-webkit-scrollbar-track {
  background: rgba(120, 106, 78, 0.12);
  border-radius: 999px;
}
.sale-view-page ::v-deep ::-webkit-scrollbar-thumb,
.sale-view-page::-webkit-scrollbar-thumb {
  background: rgba(120, 106, 78, 0.38);
  background-clip: padding-box;
  border: 2px solid transparent;
  border-radius: 999px;
}
.sale-view-page-dark,
.sale-view-page-dark ::v-deep * {
  scrollbar-color: #40515a #111b21;
  scrollbar-width: thin;
}
.sale-view-page-dark ::v-deep ::-webkit-scrollbar-track,
.sale-view-page-dark::-webkit-scrollbar-track {
  background: #111b21;
}
.sale-view-page-dark ::v-deep ::-webkit-scrollbar-thumb,
.sale-view-page-dark::-webkit-scrollbar-thumb {
  background: #40515a;
  border: 2px solid #111b21;
}
@media(max-width: 1100px) {
  .sale-view-shell {
    grid-template-columns: 1fr;
    height: auto;
    overflow: visible;
  }
  .sale-view-chat {
    min-height: 420px;
  }
  .sale-view-actions {
    overflow: visible;
  }
}
</style>
